import equal from "deep-equal";
import type { UnknownFunction, UnknownObject } from "../shared/types";

export function debounce(fn: UnknownFunction, ms = 300) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: unknown, ...args: unknown[]) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
}

export async function sleep(milliseconds: number) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export function removeParamsFromURL(url: string) {
    return url.split("?")[0];
}

export function isNumberInRange(number: number, min: number, max: number) {
    return number >= min && number <= max;
}

export function getMinOfArray(arr: number[]) {
    return arr.sort((a, b) => a - b)[0];
}

export function getMaxOfArray(arr: number[]) {
    return arr.sort((a, b) => b - a)[0];
}

/**
 * Deduplicates an array of records by a string property: values are lowercased and trimmed,
 * and the first object seen for each distinct value is kept.
 * 
 * @example 
 * const clients = [
 *   { id: 1, name: " Acme Corp " },
 *   { id: 2, name: "acme corp" },
 *   { id: 3, name: "Beta LLC" },
 * ];
 * dedupeRecordsByNormalizedStringField(clients, "name");
 * // => [{ id: 1, name: " Acme Corp " }, { id: 3, name: "Beta LLC" }]
 */
export function dedupeRecordsByNormalizedStringField<T extends object>(data: T[] | undefined, key: keyof T & string) {
    if (!data) {
        return [];
    }
    return Object.values(
        data.reduce<Record<string, T>>((acc, cv) => {
            const raw = cv[key];
            if (typeof raw !== "string") {
                return acc; // skip rows where the value is not a string
            }
            const k = raw.toLowerCase().trim();
            if (!acc[k]) acc[k] = cv;
            return acc;
        }, {}),
    );
}

/**
 * Returns a shallow patch of keys whose values differ between two plain objects.
 * Only keys present on the second object are considered; comparison uses strict equality (`===`).
 *
 * @example
 * const before = { status: "draft", count: 1, note: "x" };
 * const after = { status: "posted", count: 1, note: "y" };
 * findChangedObjectKeys(before, after);
 * // => { status: "posted", note: "y" }
 */
export function findChangedObjectKeys(o1: UnknownObject, o2: UnknownObject) {
    return Object.keys(o2).reduce(
        (diff, key) => {
            if (o1[key] === o2[key]) return diff;
            return {
                ...diff,
                [key]: o2[key],
            };
        },
        {} as Partial<UnknownObject>,
    );
}

export function deepEqual(obj1: unknown, obj2: unknown) {
    return equal(obj1, obj2);
}
